import React, { useState, useEffect } from "react";
import { Button, Form, Input, Segment, Header, Modal, Message } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import useVehicleTypeService from "../../../services/backoffice/hooks/useVehicleTypeService";
import { CreateVehicleTypeRequest, UpdateVehicleTypeRequest } from "../../../models/requests/create-vehicle-type-request";

interface VehicleTypeFormProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    vehicleTypeId?: string;  // ID para editar o tipo de veículo
}

const VehicleTypeForm: React.FC<VehicleTypeFormProps> = ({
                                                             isModal = false,
                                                             open = false,
                                                             onClose,
                                                             vehicleTypeId
                                                         }) => {
    const [type, setType] = useState<Partial<UpdateVehicleTypeRequest>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { createVehicleType, updateVehicleType, getVehicleTypeById } = useVehicleTypeService();

    useEffect(() => {
        if (vehicleTypeId) {
            setLoading(true);
            getVehicleTypeById(vehicleTypeId)
                .then((response) => {
                    setType(response);
                })
                .catch(() => {
                    setError("Erro ao carregar tipo de veículo.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [vehicleTypeId]);

    const handleSubmit = async () => {
        try {
            if (!type.code || !type.name) {
                setError("Todos os campos são obrigatórios.");
                return;
            }
            
            setLoading(true);
            if (vehicleTypeId) {
                await updateVehicleType(vehicleTypeId, { name: type.name, code: type.code });
                toast.success("Tipo de veículo atualizado com sucesso", ToasterConfig.success);
            } else {
                await createVehicleType({ name: type.name, code: type.code });
                toast.success("Tipo de veículo criado com sucesso", ToasterConfig.success);
            }
            if (onClose) onClose();
        } catch {
            setError("Erro ao salvar tipo de veículo.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setType((prevType) => ({
            ...prevType,
            [name]: value,
        }));
    };

    const handleClear = () => {
        setType({});
    };

    const isSubmitDisabled = !type.code || !type.name;

    return (
        <Segment raised style={{ maxWidth: "600px", margin: "5px auto", padding: "20px" }}>
            <Header
                as="h3"
                style={{
                    background: "#49AB3AFF",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "flex-start",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {vehicleTypeId ? "Editar Tipo de Veículo" : "Cadastro de Tipos de Veículo"}
            </Header>

            <Form onSubmit={handleSubmit} style={{ marginTop: "15px" }}>
                <div
                    style={{
                        padding: "10px",
                        border: "2px solid #d4d4d5",
                        borderRadius: "4px",
                        margin: "10px 0",
                    }}
                >
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                onChange={handleInputChange}
                                value={type.code || ""}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !type.code}
                            />
                        </Form.Field>

                        <Form.Field required>
                            <label>Tipo de Veículo</label>
                            <Input
                                name="name"
                                placeholder="Digite o tipo"
                                onChange={handleInputChange}
                                value={type.name || ""}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !type.name}
                            />
                        </Form.Field>
                    </Form.Group>
                </div>

                <div style={{ textAlign: "right" }}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {vehicleTypeId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>

                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default VehicleTypeForm;
